import React, { type RefObject } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ScrollSwapLine } from './ScrollSwapLine';
import { oneMoreThing } from '../data/oneMoreThing';
import { focusInView } from '../utils/reveal';
interface OneMoreThingProps {
  /** Pixel's second spot — he flies here from the corner and starts a new conversation */
  slotRef: RefObject<HTMLDivElement>;
}

/**
 * Back on white after the work, built like the manifesto scene: the serif teaser (which blurs over to
 * "Pixel has been watching you…"), Pixel's spot in the middle, then a huge two-line headline.
 */
export function OneMoreThing({
  slotRef
}: OneMoreThingProps) {
  const reduce = useReducedMotion();
  return <section aria-label="One more thing"
  // Phones: about half the space around "Great brands aren't seen…" (20vh instead of 38vh)
  className="relative flex w-full flex-col items-center px-3 pb-[clamp(8rem,20vh,28rem)] pt-[clamp(10rem,26vh,20rem)] text-center md:pb-[clamp(14rem,38vh,28rem)]" data-id="element-164" data-mp="3s88flbm0h7as">
      {/* Swaps around the middle: once the line's top edge reaches 55% down the screen */}
      {/* Phones: swaps 10% of the screen later, and the second line breaks as
          "What should people feel / when they meet you?" */}
      <ScrollSwapLine from={oneMoreThing.teaser} to={oneMoreThing.reveal} swapAt={0.55} mobileSwapAt={0.35} mobileBreakAfter={3}
    // This section is always white: solid black text, no blend (the blend broke mid-swap on phones)
    tone="dark" data-id="element-165" data-mp="1tw6is2vx4ja2" />

      {/* Pixel's spot — same size as in the manifesto scene, so he keeps his size; his chat renders inside it */}
      <div ref={slotRef} data-drone-dock="true"
    // Desktop: docks once the section's end rises to just below the screen's bottom edge
    // (20% of the screen earlier than the old 0.82, while Pixel is still on screen)
    data-drone-dock-at="1.02"
    // Phones: docks 15% of the screen earlier (0.97), minus the ~18vh the section's
    // bottom space shrank by, so the take-off stays at the same scroll moment
    data-drone-dock-at-mobile="0.79" data-drone-keep-scale="true"
    // Phones: same as the first chat spot — Pixel smaller on the left, chat on the right
    data-drone-mobile-left="true" className="chat-spot-mobile-left relative h-[46vh] min-h-[400px] w-full" data-id="element-166" data-mp="1696qdcum4fj8" />

      <h2 className="text-[clamp(2.75rem,calc(7.3*var(--vw)),8.75rem)] font-bold leading-[0.95] tracking-[-0.055em] text-black" data-id="element-167" data-mp="1up7mpnutsbz9">
        {oneMoreThing.heading.map((line, i) => <motion.span key={line} className="block pb-[0.05em]" {...focusInView(i * 0.2, reduce, {
        y: 14,
        blur: 14,
        amount: 0.5
      })} data-id="element-168" data-mp="24ksqnrjgfmbn" data-mpi={i}>
            {line}
          </motion.span>)}
      </h2>

      {/* Serif line under the headline, styled like "Pixel has been watching you…" and spaced like it:
          the same gap above as the section's top padding, and the same again below (pb) */}
      {/* This section is always white: solid black text, no blend mode. The blend (white + difference)
          flickered in and out on phones while scrolling, as the browser re-composited the layer. */}
      <motion.p className="mt-[clamp(8rem,20vh,28rem)] md:mt-[clamp(14rem,38vh,28rem)] px-3 font-serif font-medium md:font-normal text-[1.375rem] md:text-[clamp(1.125rem,calc(1.75*var(--vw)),1.75rem)] leading-snug tracking-[-0.01em] text-black"
    // Lower trigger amount: on phones the browser bar resizing the screen kept it from reaching 80%
    {...focusInView(0, reduce, {
      amount: 0.4
    })} data-id="element-169" data-mp="zkkuivft94ed">
        {oneMoreThing.outro}
      </motion.p>
    </section>;
}